import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { ClientMatchmakingData, MappedMatch } from '../types/matchmaking.types';

/**
 * Query key factory for matchmaking state
 */
export const matchmakingKeys = {
  all: ['matchmaking'] as const,
  client: (clientId: string | null) => [...matchmakingKeys.all, 'client', clientId] as const,
};

/**
 * Default matchmaking data
 */
const getDefaultData = (): ClientMatchmakingData => ({
  query: '',
  matches: [],
  currentPage: 0,
  hasMore: true,
});

/**
 * Hook to manage matchmaking state per client using React Query cache
 * 
 * This replaces Redux state management with React Query's cache,
 * providing persistence across tab changes and better performance.
 * 
 * Uses React Query's cache with long staleTime and gcTime to ensure
 * data persists when users switch tabs and come back.
 * 
 * @param clientId - Current client ID
 * @returns State management functions and current state
 */
export const useMatchmakingState = (clientId: string | null) => {
  const queryClient = useQueryClient();

  // Use React Query to manage and cache the state
  // This ensures data persists across tab changes
  const { data } = useQuery({
    queryKey: matchmakingKeys.client(clientId),
    queryFn: () => getDefaultData(),
    enabled: !!clientId,
    staleTime: Infinity, // Data never becomes stale - persists until manually updated
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    initialData: getDefaultData,
  });

  /**
   * Get current matchmaking data for the client
   */
  const getData = useCallback((): ClientMatchmakingData => {
    if (!clientId) return getDefaultData();
    
    const queryKey = matchmakingKeys.client(clientId);
    const cachedData = queryClient.getQueryData<ClientMatchmakingData>(queryKey);
    
    return cachedData || getDefaultData();
  }, [clientId, queryClient]);

  /**
   * Set client query (search prompt)
   */
  const setClientQuery = useCallback((query: string) => {
    if (!clientId) return;
    
    const queryKey = matchmakingKeys.client(clientId);
    const currentData = getData();
    
    queryClient.setQueryData<ClientMatchmakingData>(queryKey, {
      ...currentData!,
      query,
    });
  }, [clientId, queryClient, getData]);

  /**
   * Set client matches (results)
   */
  const setClientMatches = useCallback((params: {
    matches: MappedMatch[];
    append?: boolean;
    hasMore?: boolean;
  }) => {
    if (!clientId) return;
    
    const queryKey = matchmakingKeys.client(clientId);
    const currentData = getData();
    
    const { matches, append = false, hasMore = true } = params;
    
    const updatedData: ClientMatchmakingData = {
      query: currentData?.query || '',
      matches: append 
        ? [...(currentData?.matches || []), ...matches]
        : matches,
      currentPage: append 
        ? (currentData?.currentPage || 0) + 1
        : 1,
      hasMore,
    };
    
    queryClient.setQueryData<ClientMatchmakingData>(queryKey, updatedData);
  }, [clientId, queryClient, getData]);

  /**
   * Clear client matches
   */
  const clearClientMatches = useCallback(() => {
    if (!clientId) return;
    
    const queryKey = matchmakingKeys.client(clientId);
    const currentData = getData();
    
    queryClient.setQueryData<ClientMatchmakingData>(queryKey, {
      ...currentData!,
      matches: [],
      currentPage: 0,
      hasMore: true,
    });
  }, [clientId, queryClient, getData]);

  /**
   * Clear client query
   */
  const clearClientQuery = useCallback(() => {
    if (!clientId) return;
    
    const queryKey = matchmakingKeys.client(clientId);
    const currentData = getData();
    
    queryClient.setQueryData<ClientMatchmakingData>(queryKey, {
      ...currentData!,
      query: '',
    });
  }, [clientId, queryClient, getData]);

  /**
   * Reset all client data
   */
  const resetClientData = useCallback(() => {
    if (!clientId) return;
    
    const queryKey = matchmakingKeys.client(clientId);
    queryClient.removeQueries({ queryKey });
  }, [clientId, queryClient]);

  // Get current state from React Query cache
  const currentData = data || getData();

  return {
    // Current state
    query: currentData.query || '',
    matches: currentData.matches || [],
    hasMore: currentData.hasMore ?? true,
    currentPage: currentData.currentPage || 0,
    
    // Actions
    setClientQuery,
    setClientMatches,
    clearClientMatches,
    clearClientQuery,
    resetClientData,
  };
};

